import type { Character, EntryKind, ParseIssue, ParseResult, ParserMode, ScriptDocument, ScriptEntry, ScriptPage, ScriptSection } from "./types";

const id = (): string => {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === "function") return webCrypto.randomUUID();

  // randomUUID is restricted to secure contexts in mobile browsers. LAN preview
  // servers commonly use HTTP, so retain unique IDs when the API is unavailable.
  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === "function") webCrypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};
const PAGE = /^#\s*Page\s*(\d+)\s*$/i;
const LEFT = /^([@]?[\p{Lu}][\p{L}\p{N}.' -]{0,30}?)(?:\s*[:–—]\s*|\s{2,})(.+)$/u;
const LAYOUT_LEFT = /^(.{1,120}?):\s+(.+)$/u;
const SPACED_LAYOUT_LEFT = /^.{1,120}?:[ \t]{2,}\S/u;
const TAG = /^@(?:"([^"]+)"|'([^']+)'|(\S+))\s+(.+)$/u;
const SPEAKER_ONLY = /^([\p{Lu}][\p{L}\p{N}.' -]{0,30})[:.]?$/u;
const STAGE = /^\s*[[(].+[\])]\s*$/;
const HEADING = /^(?:[A-Z][A-Z\s&'-]{2,}|(?:Act|Scene|Charge)\b.*)$/;
const SECTION_HEADING = /^(?:PRAYER|OBLIGATION|CHARGE|ACT\b.*|SCENE\b.*)$/i;
const DIRECTION_CUE = /\s+(?:after|answers?|as|at|before|facing|gives?|goes?|having|holding|indicates?|on|raises?|rises?|says?|speaks?|takes?|to|turns?|with|who)\b/i;

function layoutDirection(value: string, label: string): string | undefined {
  let direction = value.slice(label.length).trim().replace(/^,\s*/, "");
  if (direction.startsWith("(") && direction.endsWith(")")) direction = direction.slice(1, -1).trim();
  direction = direction.replace(/^(TO|OR)\b/, (word) => word.toLowerCase());
  return direction || undefined;
}

function layoutSpeaker(prefix: string): { label: string; direction?: string } | undefined {
  const value = prefix.trim();
  const code = value.match(/^((?:[\p{Lu}]\.){1,8}|[\p{Lu}]{2,10})(?=$|[\s,([])/u);
  if (code) {
    const label = code[1]!;
    const direction = layoutDirection(value, label);
    return { label, ...(direction ? { direction } : {}) };
  }

  const uncluttered = value.split(/\s*[,([]/, 1)[0]?.trim() ?? "";
  const cue = uncluttered.search(DIRECTION_CUE);
  const label = (cue < 0 ? uncluttered : uncluttered.slice(0, cue)).trim();
  const words = label.split(/\s+/);
  if (!label || words.length > 5) return undefined;
  if (!words.every((word) => /^(?:or|[\p{Lu}][\p{L}.'’\-]*)$/u.test(word))) return undefined;
  const direction = layoutDirection(value, label);
  return { label, ...(direction ? { direction } : {}) };
}

function layoutDialogue(value: string): { label: string; dialogue: string } | undefined {
  const match = value.match(LAYOUT_LEFT);
  if (!match) return undefined;
  const speaker = layoutSpeaker(match[1]!);
  if (!speaker) return undefined;
  const dialogue = speaker.direction ? `(${speaker.direction}) ${match[2]}` : match[2]!;
  return { label: speaker.label, dialogue };
}

function layoutSpeakerOnly(value: string): { label: string; direction?: string } | undefined {
  const match = value.match(/^(.{1,120}?):\s*$/u);
  return match ? layoutSpeaker(match[1]!) : undefined;
}

function hasLayoutStructure(lines: string[]): boolean {
  const content = lines.map((line) => line.trim()).filter(Boolean);
  return content.filter((line) => layoutDialogue(line)).length >= 3
    && content.filter((line) => SPACED_LAYOUT_LEFT.test(line) && layoutDialogue(line)).length >= 2;
}

function isLayoutHeading(value: string): boolean {
  if (value.length > 80 || /[.!?;:]$/.test(value)) return false;
  const words = value.split(/\s+/);
  if (words.length > 10) return false;
  const significant = words.filter((word) => !/^(?:a|an|and|for|in|of|the|to)$/i.test(word));
  return significant.length > 0 && significant.every((word) => /^(?:\d{4}|[\p{Lu}][\p{L}.'’\-]*)$/u.test(word));
}

function canContinue(item: ScriptEntry | undefined): item is ScriptEntry {
  return Boolean(item && (item.kind === "dialogue" || item.kind === "stageDirection") && !/[.!?…:;)\]]$/.test(item.text));
}

function speakerKey(label: string): string {
  const normalized = label.replace(/^@/, "").replace(/:$/, "").replace(/\s+/g, " ").trim().toUpperCase();
  return /^[\p{Lu}]{2,}\.$/u.test(normalized) ? normalized.slice(0, -1) : normalized;
}

export function detectParserMode(text: string): Exclude<ParserMode, "auto"> {
  const lines = text.split(/\r?\n/).filter(Boolean).slice(0, 100);
  const tag = lines.filter((line) => TAG.test(line)).length;
  const left = lines.filter((line) => LEFT.test(line)).length;
  const layoutLeft = lines.filter((line) => layoutDialogue(line.trim())).length;
  const layoutLabels = lines.filter((line) => layoutSpeakerOnly(line.trim())).length;
  const top = lines.filter((line, i) => SPEAKER_ONLY.test(line.trim()) && Boolean(lines[i + 1])).length;
  if (tag >= 2 && tag >= left) return "tag";
  if (layoutLeft >= 3 || layoutLabels >= 2 || left >= 2) return "left";
  if (top >= 2) return "top";
  return "none";
}

function entry(kind: EntryKind, text: string, rawText: string, pageNumber?: number): ScriptEntry {
  return { id: id(), kind, text: text.trim(), rawText, ...(pageNumber === undefined ? {} : { pageNumber }) };
}

export function parseScript(sourceText: string, requestedMode: ParserMode = "auto", importMode: "file" | "text" = "text", suggestedTitle = "Untitled Script"): ParseResult {
  const parserMode = requestedMode === "auto" ? detectParserMode(sourceText) : requestedMode;
  const lines = sourceText.replace(/^\uFEFF/, "").split(/\r?\n/);
  const layoutStructure = parserMode === "left" && hasLayoutStructure(lines);
  const issues: ParseIssue[] = [];
  const entries: ScriptEntry[] = [];
  const characters = new Map<string, Character>();
  let pendingSpeaker: string | undefined;
  let pendingDirection: string | undefined;
  let pageNumber: number | undefined;
  let title = suggestedTitle.replace(/\.(txt|md|fountain|fdx|docx|pdf)$/i, "").trim() || "Untitled Script";

  const characterFor = (label: string): Character => {
    const key = speakerKey(label);
    let character = characters.get(key);
    if (!character) {
      character = { id: id(), name: label.replace(/^@/, "").replace(/:$/, "").trim(), aliases: [key], colour: `hsl(${(characters.size * 67 + 250) % 360} 65% 62%)` };
      characters.set(key, character);
    }
    return character;
  };

  lines.forEach((raw, index) => {
    const value = raw.trim();
    if (!value) return;
    const page = value.match(PAGE);
    if (page) {
      pageNumber = Number(page[1]);
      entries.push(entry("pageMarker", `Page ${pageNumber}`, raw, pageNumber));
      return;
    }
    if (entries.length === 0 && !(layoutStructure ? layoutDialogue(value) : LEFT.test(value)) && !layoutSpeakerOnly(value) && !TAG.test(value) && !STAGE.test(value)) {
      title = value.replace(/^#+\s*/, "");
      entries.push(entry("heading", title, raw, pageNumber));
      return;
    }

    let label: string | undefined;
    let dialogue: string | undefined;
    if (parserMode === "tag") {
      const match = value.match(TAG);
      if (match) {
        label = match[1] ?? match[2] ?? match[3];
        dialogue = match[4];
      }
    } else if (parserMode === "left") {
      const speakerOnly = layoutSpeakerOnly(value);
      if (speakerOnly) {
        if (pendingSpeaker) issues.push({ line: index + 1, type: "unknown-speaker", message: `No dialogue found after ${pendingSpeaker}.` });
        pendingSpeaker = speakerOnly.label;
        pendingDirection = speakerOnly.direction;
        return;
      }
      if (pendingSpeaker) {
        label = pendingSpeaker;
        dialogue = pendingDirection ? `(${pendingDirection}) ${value}` : value;
        pendingSpeaker = undefined;
        pendingDirection = undefined;
      } else {
        const layout = layoutDialogue(value);
        if (layout) ({ label, dialogue } = layout);
        else {
          const match = value.match(LEFT);
          if (match) [, label, dialogue] = match;
        }
      }
    } else if (parserMode === "top") {
      if (SPEAKER_ONLY.test(value) && value === value.toLocaleUpperCase() && !SECTION_HEADING.test(value) && value.split(/\s+/).length <= 4) {
        pendingSpeaker = value;
        return;
      }
      if (pendingSpeaker) {
        label = pendingSpeaker;
        dialogue = value;
        pendingSpeaker = undefined;
      }
    } else if (parserMode === "none") {
      label = "Reader";
      dialogue = value;
    }

    if (label && dialogue) {
      const character = characterFor(label);
      entries.push({ ...entry("dialogue", dialogue, raw, pageNumber), speakerId: character.id, speakerLabel: character.name });
    } else if (STAGE.test(value)) {
      entries.push(entry("stageDirection", value, raw, pageNumber));
    } else if (HEADING.test(value) || (layoutStructure && isLayoutHeading(value))) {
      entries.push(entry("heading", value, raw, pageNumber));
    } else if (layoutStructure && canContinue(entries.at(-1))) {
      const previous = entries.at(-1)!;
      previous.text = `${previous.text} ${value}`;
      previous.rawText = `${previous.rawText}\n${raw}`;
    } else if (layoutStructure) {
      entries.push(entry("stageDirection", value, raw, pageNumber));
    } else {
      entries.push(entry("note", value, raw, pageNumber));
      if (parserMode !== "none" && value.length > 80) issues.push({ line: index + 1, type: "broken-line", message: "Long unassigned line; check its speaker." });
    }
  });

  if (pendingSpeaker) issues.push({ line: lines.length, type: "unknown-speaker", message: `No dialogue found after ${pendingSpeaker}.` });

  const pagesByNumber = new Map<number, ScriptPage>();
  const unnumbered: ScriptPage = { id: id(), entries: [] };
  for (const item of entries) {
    if (item.pageNumber === undefined) unnumbered.entries.push(item);
    else {
      let page = pagesByNumber.get(item.pageNumber);
      if (!page) { page = { id: id(), pageNumber: item.pageNumber, entries: [] }; pagesByNumber.set(item.pageNumber, page); }
      page.entries.push(item);
    }
  }
  const pages = [...(unnumbered.entries.length ? [unnumbered] : []), ...pagesByNumber.values()];
  if (!pages.length) pages.push(unnumbered);

  const sections: ScriptSection[] = [];
  let section: ScriptSection | undefined;
  for (const item of entries) {
    if (item.kind === "heading") {
      section = { id: id(), title: item.text, entries: [] };
      sections.push(section);
    } else if (section) section.entries.push(item);
  }

  const now = new Date().toISOString();
  const document: ScriptDocument = {
    id: id(), title, sourceText, importMode, parserMode, pages,
    characters: [...characters.values()], sections, memorisation: {}, tags: [], notes: "",
    settings: {
      selectedCharacterIds: [], revealMode: "normal", hideMine: false, hideOthers: false, showOtherLinesNormally: false,
      voiceReading: { readOthersLines: true, readMyLines: false, autoProceed: false, speed: 1, pitch: 1, volume: 1, language: "en-GB" },
      recognition: { threshold: 0.4, ignoredWords: ["um", "uh", "er"] },
    },
    createdAt: now, updatedAt: now,
  };
  return { document, issues };
}

export function allEntries(document: ScriptDocument): ScriptEntry[] {
  return document.pages.flatMap((page) => page.entries);
}

export function rebuildSections(document: ScriptDocument): void {
  const sections: ScriptSection[] = [];
  let section: ScriptSection | undefined;
  for (const item of allEntries(document)) {
    if (item.kind === "heading") {
      section = { id: id(), title: item.text, entries: [] };
      sections.push(section);
    } else if (section) section.entries.push(item);
  }
  document.sections = sections;
}
